import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { MuiTelInput, type MuiTelInputCountry } from 'mui-tel-input';
import type { SxProps, Theme } from '@mui/material';

export interface RHFPhoneInputProps<T extends FieldValues> {
  /** Field name - must match form schema */
  name: FieldPath<T>;
  /** React Hook Form control object */
  control: Control<T>;
  /** Field label */
  label?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Default country code (ISO 3166-1 alpha-2) */
  defaultCountry?: MuiTelInputCountry;
  /** Full width of parent container */
  fullWidth?: boolean;
  /** MUI sx prop for custom styling */
  sx?: SxProps<Theme>;
  /** Data test ID for testing */
  'data-testid'?: string;
}

/**
 * Phone input integrated with React Hook Form using MuiTelInput.
 * Defaults to UK (+44) format.
 *
 * @example
 * ```tsx
 * <RHFPhoneInput
 *   name="primaryPhoneNumber"
 *   control={form.control}
 *   label="Phone Number"
 *   required
 * />
 * ```
 */
export const RHFPhoneInput = <T extends FieldValues>({
  name,
  control,
  label,
  helperText,
  disabled,
  required,
  defaultCountry = 'GB',
  fullWidth,
  sx,
  'data-testid': testId,
}: RHFPhoneInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, value, onChange, onBlur },
        fieldState: { error },
      }) => (
        <MuiTelInput
          value={value ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          inputRef={ref}
          label={label}
          disabled={disabled}
          required={required}
          defaultCountry={defaultCountry}
          fullWidth={fullWidth}
          error={!!error}
          helperText={error?.message || helperText}
          sx={sx}
          slotProps={{
            htmlInput: { 'data-testid': testId },
          }}
        />
      )}
    />
  );
};
